<div class="glass-selector" [class.is-disabled]="isDisabled">
  <div class="selector-header">
    <div>
      <h3>{{ "patientCreate.woundAssessment.glass.title" | translate }}</h3>
      <p>{{ "patientCreate.woundAssessment.glass.description" | translate }}</p>
    </div>
  </div>

  <div class="dependency-banner" *ngIf="disabledMessage">
    {{ disabledMessage | translate }}
  </div>

  <div class="interaction-banner" *ngIf="interactionNotice">
    {{ interactionNotice }}
  </div>

  <div class="viewer-layout">
    <div class="viewer-shell">
      <div
        class="viewer-stage"
        (mousedown)="onDragStart($event)"
        (mousemove)="onDragMove($event)"
        (mouseup)="onDragEnd()"
        (mouseleave)="onDragEnd()"
        (wheel)="onWheel($event)"
      >
        <div class="viewer-toolbar">
          <button type="button" (click)="setFrontView()">
            {{ "patientCreate.woundAssessment.glass.viewer.front" | translate }}
          </button>
          <button type="button" (click)="setBackView()">
            {{ "patientCreate.woundAssessment.glass.viewer.back" | translate }}
          </button>
          <button type="button" (click)="resetView()">
            {{ "patientCreate.woundAssessment.glass.viewer.reset" | translate }}
          </button>
        </div>

        <div class="scene-legend-shell">
          <div class="scene-legend">
            <div class="scene-legend-title">
              {{ "patientCreate.woundAssessment.glass.legend.title" | translate }}
            </div>
            <div class="scene-legend-row">
              <span class="scene-legend-swatch selected"></span>
              <span>{{ "patientCreate.woundAssessment.glass.legend.selected" | translate }}</span>
            </div>
            <div class="scene-legend-row">
              <span class="scene-legend-swatch artery"></span>
              <span>{{ "patientCreate.woundAssessment.glass.legend.artery" | translate }}</span>
            </div>
            <div class="scene-legend-row">
              <span class="scene-legend-swatch posterior"></span>
              <span>{{ "patientCreate.woundAssessment.glass.legend.deepPosterior" | translate }}</span>
            </div>
            <label class="scene-legend-toggle">
              <input type="checkbox" [checked]="showVeins" (change)="toggleVeinVisibility()" />
              <span>{{ "patientCreate.woundAssessment.glass.legend.veins" | translate }}</span>
            </label>
            <label class="scene-legend-toggle">
              <input type="checkbox" [checked]="showOtherArteries" (change)="toggleOtherArteriesVisibility()" />
              <span>{{ "patientCreate.woundAssessment.glass.legend.otherArteries" | translate }}</span>
            </label>
            <div class="scene-legend-note">
              {{ "patientCreate.woundAssessment.glass.scoringScopeNote" | translate }}
            </div>
          </div>
        </div>

        <div class="region-menu-shell">
          <button type="button" class="region-menu-toggle" (click)="toggleRegionMenu($event)">
            <span>
              <strong>{{ "patientCreate.woundAssessment.glass.regionMenu.addRegion" | translate }}</strong>
              <small>
                {{
                  selectedVessels.length
                    ? ("patientCreate.woundAssessment.glass.regionMenu.selectedCount"
                      | translate: { count: selectedVessels.length })
                    : ("patientCreate.woundAssessment.glass.menuHint" | translate)
                }}
              </small>
            </span>
            <i class="fas" [class.fa-chevron-down]="!isRegionMenuOpen" [class.fa-chevron-up]="isRegionMenuOpen"></i>
          </button>

          <div class="region-menu" *ngIf="isRegionMenuOpen">
            <ng-container *ngFor="let group of regionMenuGroups">
              <div class="region-menu-group" *ngIf="group.vessels.length">
                <div class="region-menu-group-title">{{ getVesselGroupLabel(group.group) }}</div>
                <button
                  type="button"
                  class="region-menu-item"
                  *ngFor="let vessel of group.vessels"
                  [class.selected]="isRegionSelected(vessel.id)"
                  (mousedown)="selectVesselFromMenu(vessel, $event)"
                >
                  <span class="region-menu-item-label">
                    <strong *ngIf="vessel.side !== 'C'">{{ vessel.side }}</strong>
                    {{ getVesselDisplayLabel(vessel.id) }}
                  </span>
                  <i class="fas fa-check" *ngIf="isRegionSelected(vessel.id)"></i>
                </button>
              </div>
            </ng-container>
          </div>
        </div>

        <div class="scene-frame">
          <div class="three-viewport" #threeViewport [class.active]="viewerMode === '3d'">
            <canvas #threeCanvas class="three-canvas"></canvas>
            <div #hoverLabel class="hover-label"></div>
          </div>

          <div class="scene-2d" [class.active]="viewerMode === '2d'">
            <div class="scene" [class.is-flat]="viewerMode === '2d'" [style.transform]="viewTransform">
              <svg
                class="scene-layer posterior-layer"
                [attr.viewBox]="'0 0 ' + viewerSizes.width + ' ' + viewerSizes.height"
                preserveAspectRatio="xMidYMid meet"
              >
                <g *ngFor="let region of regions">
                  <ng-container *ngIf="region.layer === 'posterior'">
                    <path
                      class="vessel-hit"
                      [attr.d]="region.path"
                      [attr.data-id]="region.id"
                      (mouseenter)="onRegionHover(region)"
                      (mouseleave)="onRegionLeave()"
                      (click)="toggleRegion(region, $event)"
                    />
                    <path
                      class="vessel vessel-posterior"
                      [attr.d]="region.path"
                      [attr.data-id]="region.id"
                      [class.selected]="isRegionSelected(region.id)"
                      [class.hovered]="hoveredRegionId2d === region.id"
                    />
                  </ng-container>
                </g>
              </svg>

              <svg
                class="scene-layer body-layer"
                [attr.viewBox]="'0 0 ' + viewerSizes.width + ' ' + viewerSizes.height"
                preserveAspectRatio="xMidYMid meet"
              >
                <defs>
                  <linearGradient id="glassBodyGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stop-color="#2a2c29" stop-opacity=".85" />
                    <stop offset=".45" stop-color="#d7d3cc" stop-opacity=".22" />
                    <stop offset="1" stop-color="#2a2c29" stop-opacity=".85" />
                  </linearGradient>
                </defs>

                <path
                  class="body-shape"
                  d="M 280 24 C 310 24 328 50 328 86 C 328 112 318 134 304 144 L 300 162 C 318 168 334 174 346 188 C 360 200 372 220 380 244 C 386 268 388 292 384 318 C 380 344 374 370 370 392 L 366 422 C 366 442 366 460 370 478 L 374 502 C 378 518 380 532 380 548 C 378 580 374 624 368 668 L 364 702 C 360 722 358 742 360 766 C 362 790 360 818 356 842 L 352 866 L 388 884 L 320 884 L 320 866 C 318 842 318 814 322 790 C 326 766 326 740 322 706 L 318 668 C 312 624 308 580 304 548 L 296 528 C 290 514 286 510 280 510 C 274 510 270 514 264 528 L 256 548 C 252 580 248 624 242 668 L 238 706 C 234 740 234 766 238 790 C 242 814 242 842 240 866 L 240 884 L 172 884 L 208 866 L 204 842 C 200 818 198 790 200 766 C 202 742 200 722 196 702 L 192 668 C 186 624 182 580 180 548 C 180 532 182 518 186 502 L 190 478 C 194 460 194 442 194 422 L 190 392 C 186 370 180 344 176 318 C 172 292 174 268 180 244 C 188 220 200 200 214 188 C 226 174 242 168 260 162 L 256 144 C 242 134 232 112 232 86 C 232 50 250 24 280 24 Z"
                />
              </svg>

              <svg
                class="scene-layer anterior-layer"
                [attr.viewBox]="'0 0 ' + viewerSizes.width + ' ' + viewerSizes.height"
                preserveAspectRatio="xMidYMid meet"
              >
                <g *ngFor="let region of regions">
                  <ng-container *ngIf="region.layer === 'anterior'">
                    <path
                      class="vessel-hit"
                      [attr.d]="region.path"
                      [attr.data-id]="region.id"
                      (mouseenter)="onRegionHover(region)"
                      (mouseleave)="onRegionLeave()"
                      (click)="toggleRegion(region, $event)"
                    />
                    <path
                      class="vessel vessel-anterior"
                      [attr.d]="region.path"
                      [attr.data-id]="region.id"
                      [class.selected]="isRegionSelected(region.id)"
                      [class.hovered]="hoveredRegionId2d === region.id"
                    />
                  </ng-container>
                </g>
              </svg>

              <div class="label-layer">
                <button
                  type="button"
                  class="region-label"
                  *ngFor="let region of regions"
                  [class.visible]="isRegionLabelVisible(region.id)"
                  [style.left.%]="(region.labelX / viewerSizes.width) * 100"
                  [style.top.%]="(region.labelY / viewerSizes.height) * 100"
                  (mouseenter)="onRegionHover(region)"
                  (mouseleave)="onRegionLeave()"
                  (click)="toggleRegion(region, $event)"
                >
                  <span *ngIf="region.side !== 'C'">{{ region.side }}</span>
                  {{ ("patientCreate.woundAssessment.glass.regions." + region.labelKey) | translate }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="selection-overlay" *ngIf="viewerMode === '3d' && selectedVessels.length">
          <span class="selection-overlay-label">
            {{ "patientCreate.woundAssessment.glass.selectedRegions" | translate }}
          </span>
          <strong class="selection-overlay-value">{{ selectedRegionSummary }}</strong>
          <div class="selection-chips">
            <button
              type="button"
              class="selection-chip"
              *ngFor="let vessel of selectedVessels"
              (click)="removeRegion(vessel, $event)"
            >
              <span class="selection-chip-badge">{{ vessel.group }}</span>
              <span class="selection-chip-label">
                <strong *ngIf="vessel.side !== 'C'">{{ vessel.side }}</strong>
                {{ getVesselDisplayLabel(vessel.id) }}
              </span>
              <i class="fas fa-xmark"></i>
            </button>
          </div>
        </div>
      </div>

      <p class="viewer-hint">
        {{ "patientCreate.woundAssessment.glass.viewer.hint" | translate }}
      </p>
    </div>

    <!-- selection-panel: şimdilik gizli, gerektiğinde açılabilir
    <div class="selection-panel">
      <div class="panel-card">
        <div class="panel-title">
          {{ "patientCreate.woundAssessment.glass.selectedRegions" | translate }}
        </div>
        <div class="selected-regions" *ngIf="selectedVessels.length; else emptySelection">
          <button
            type="button"
            class="region-chip"
            *ngFor="let vessel of selectedVessels"
            (click)="removeRegion(vessel, $event)"
          >
            <span *ngIf="vessel.side !== 'C'">{{ vessel.side }}</span>
            {{ getVesselDisplayLabel(vessel.id) }}
          </button>
        </div>
        <ng-template #emptySelection>
          <div class="empty-state">
            {{ "patientCreate.woundAssessment.glass.emptySelection" | translate }}
          </div>
        </ng-template>

        <button type="button" class="clear-button" (click)="clearSelection()">
          {{ "patientCreate.woundAssessment.glass.clearSelection" | translate }}
        </button>
      </div>

      <div class="panel-card summary-card">
        <div class="summary-row">
          <span>{{ "patientCreate.woundAssessment.glass.summary.wifiStage" | translate }}</span>
          <strong>{{ wifiStage ? ("patientCreate.woundAssessment.wifi.stages." + wifiStage | translate) : "—" }}</strong>
        </div>
        <div class="summary-row">
          <span>{{ "patientCreate.woundAssessment.glass.summary.finalStage" | translate }}</span>
          <strong>{{ glassStage ? ("patientCreate.woundAssessment.glass.stages." + glassStage | translate) : "—" }}</strong>
        </div>
        <div class="summary-row">
          <span>{{ "patientCreate.woundAssessment.glass.summary.recommendation" | translate }}</span>
          <strong>{{
            treatmentRecommendation
              ? ("patientCreate.woundAssessment.glass.recommendations." + treatmentRecommendation | translate)
              : "—"
          }}</strong>
        </div>
      </div>
    </div>
    -->
  </div>

  <div class="segment-sections">
    <ng-container *ngFor="let segment of orderedSegments">
      <ng-container
        *ngIf="
          hasVisibleSegment(segment) &&
          getQuestionByShortCode(getShortCodeForSegment(segment)) as question
        "
      >
        <div
          class="glass-question-wrapper"
          [class.is-disabled]="question.isDisabled || isDisabled"
        >
          <div
            class="glass-disabled-tooltip"
            *ngIf="question.isDisabled || isDisabled"
          >
            {{ (question.disabledReason || disabledMessage || "") | translate }}
          </div>

          <div
            class="glass-question-body"
            [class.disabled-question]="question.isDisabled || isDisabled"
          >
            <div
              class="glass-question-title bg-dark p-3 rounded text-light fs-6 fw-semibold mb-2 d-flex justify-content-between align-items-center"
            >
              <span>{{ getQuestionDisplayText(question.text) }}</span>
              <span
                class="badge badge-light-secondary ms-2"
                *ngIf="question.isDisabled || isDisabled"
                >{{ "assessment.disabled" | translate }}</span
              >
            </div>

            <div class="d-flex gap-3 flex-wrap glass-form-check-list">
              <div class="form-check" *ngFor="let option of question.options">
                <input
                  class="form-check-input"
                  type="radio"
                  [name]="'q-' + question.id"
                  [id]="'glass-opt-' + question.id + '-' + option.id"
                  [value]="option.id"
                  [checked]="isSelectedOption(question, option.id)"
                  [disabled]="question.isDisabled || isDisabled"
                  (change)="selectOption(question, option.id)"
                />
                <label
                  class="form-check-label text-light"
                  [class.cursor-not-allowed]="question.isDisabled || isDisabled"
                  [for]="'glass-opt-' + question.id + '-' + option.id"
                >
                  {{
                    getOptionBadge(question, option.id) +
                      " - " +
                      getOptionDescription(option.text)
                  }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </ng-container>
    </ng-container>
  </div>
</div>
